import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

/**
 * Singleton cache for auth state to prevent redundant API calls
 * Deduplicates concurrent requests and caches results for 5 seconds
 */
class AuthCache {
  private userPromise: Promise<User | null> | null = null;
  private sessionPromise: Promise<Session | null> | null = null;
  private userCache: User | null = null;
  private sessionCache: Session | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_TTL = 5000; // 5 seconds

  /**
   * Get current user with request deduplication
   * Returns cached value if fresh, or reuses in-flight request
   */
  async getUser(): Promise<User | null> {
    const now = Date.now();
    
    // Return cached value if fresh
    if (this.userCache && (now - this.lastFetch < this.CACHE_TTL)) {
      return this.userCache;
    }

    // Return in-flight promise if exists (prevents concurrent requests)
    if (this.userPromise) {
      return this.userPromise.then(() => this.userCache);
    }

    // Create new request
    this.userPromise = supabase.auth.getUser()
      .then(({ data, error }) => {
        if (error) throw error;
        this.userCache = data.user;
        this.lastFetch = now;
        this.userPromise = null;
        return data.user;
      })
      .catch((error) => {
        console.error('[AuthCache] getUser error:', error);
        this.userPromise = null;
        return null;
      });

    return this.userPromise;
  }

  /**
   * Get current session with request deduplication
   */
  async getSession(): Promise<Session | null> {
    const now = Date.now();
    
    if (this.sessionCache && (now - this.lastFetch < this.CACHE_TTL)) {
      return this.sessionCache;
    }

    if (this.sessionPromise) {
      return this.sessionPromise.then(() => this.sessionCache);
    }

    this.sessionPromise = supabase.auth.getSession()
      .then(({ data, error }) => {
        if (error) throw error;
        this.sessionCache = data.session;
        this.userCache = data.session?.user ?? null;
        this.lastFetch = now;
        this.sessionPromise = null;
        return data.session;
      })
      .catch((error) => {
        // Don't log "session missing" as error - it's normal for logged-out users
        if (error?.name !== 'AuthSessionMissingError') {
          console.error('[AuthCache] getSession error:', error);
        }
        this.sessionPromise = null;
        return null;
      });

    return this.sessionPromise;
  }

  /**
   * Clear cache - call on logout
   */
  clear() {
    this.userCache = null;
    this.sessionCache = null;
    this.userPromise = null;
    this.sessionPromise = null;
    this.lastFetch = 0;
  }

  /**
   * Update cache - call on login/auth state change
   */
  update(user: User | null, session: Session | null) {
    this.userCache = user;
    this.sessionCache = session;
    this.lastFetch = Date.now();
  }

  /**
   * Get cached values synchronously (no API call)
   */
  getCached() {
    return {
      user: this.userCache,
      session: this.sessionCache,
      isFresh: Date.now() - this.lastFetch < this.CACHE_TTL,
    };
  }
}

export const authCache = new AuthCache();
